import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, GoogleLogo, TrendUp } from "@phosphor-icons/react";

const MONTH_NAMES = ["janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

/**
 * Motivational rating badge in the AlertesPanel header.
 * Shows average stars, review count this month, and Google click-through.
 * Silently returns nothing while no ratings have been collected yet.
 */
export default function SavRatingBadge() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/sav-tickets/rating-stats");
        if (alive) setStats(r.data);
      } catch (e) {
        console.debug("[sav-rating-badge] stats fetch failed:", e?.message || e);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!stats || !stats.alltime || stats.alltime.count === 0) return null;

  const monthLabel = MONTH_NAMES[new Date().getMonth()];
  const { alltime, month } = stats;
  const monthCount = month.count || 0;
  const monthAvg = month.avg || 0;
  const showMonth = monthCount > 0;
  const displayAvg = showMonth ? monthAvg : alltime.avg;

  // Color the badge based on average
  const tone =
    displayAvg >= 4.5 ? { bg: "from-amber-500/20 to-emerald-500/15", border: "border-amber-400/40", star: "text-amber-300", label: "text-amber-100" }
    : displayAvg >= 3.5 ? { bg: "from-amber-500/15 to-white/5", border: "border-amber-400/25", star: "text-amber-300", label: "text-amber-100" }
    : { bg: "from-white/8 to-white/[0.02]", border: "border-white/15", star: "text-white/60", label: "text-white/70" };

  const conversionRate = alltime.count > 0
    ? Math.round((alltime.google_clicks / alltime.count) * 100)
    : 0;

  return (
    <div
      className={`group relative rounded-xl bg-gradient-to-br ${tone.bg} border ${tone.border} px-3 py-2 flex items-center gap-2.5 hover:scale-[1.02] transition-transform cursor-default`}
      data-testid="sav-rating-badge"
      title={`Moyenne ${alltime.avg.toFixed(2)}/5 sur ${alltime.count} avis · ${alltime.google_clicks} passages Google (${conversionRate}%)`}
    >
      <Star size={18} weight="fill" className={`${tone.star} drop-shadow-[0_0_6px_rgba(251,191,36,0.4)]`} />
      <div className="leading-tight">
        <div className={`font-display font-bold text-base ${tone.label} flex items-baseline gap-1`}>
          {displayAvg.toFixed(1)}
          <span className="text-[9px] font-normal text-white/40 uppercase tracking-wider">/5</span>
        </div>
        <div className="text-[9.5px] text-white/50 flex items-center gap-1">
          {showMonth
            ? <>{monthCount} avis en {monthLabel}</>
            : <>{alltime.count} avis au total</>}
        </div>
      </div>
      {alltime.google_clicks > 0 && (
        <div
          className="hidden sm:flex items-center gap-1 ml-1 pl-2.5 border-l border-white/10 text-[9.5px] text-white/55"
          title={`${alltime.google_clicks} clients ont ouvert le lien Google (${conversionRate}%)`}
        >
          <GoogleLogo size={11} weight="bold" className="text-white/50" />
          <span className="font-semibold text-white/80">{alltime.google_clicks}</span>
          <TrendUp size={9} weight="bold" className="text-emerald-400" />
        </div>
      )}
    </div>
  );
}
